<script lang="ts">
  interface Point {
    x: number;
    y: number;
    day: string;
    value: number;
  }
  interface Series {
    label: string;
    color: string;
    path: string;
    area?: string;
    points: Point[];
    dashed?: boolean;
  }
  let { label, series }: { label: string; series: Series[] } = $props();
  let selected = $state<number | null>(null);
  const points = $derived(series[0]?.points ?? []);
  const index = $derived(
    selected === null || !points.length ? null : Math.min(selected, points.length - 1),
  );
  const point = $derived(index === null ? null : points[index]);
  const gradient = $props.id();
  function move(event: PointerEvent) {
    const rect =
      event.currentTarget instanceof HTMLElement
        ? event.currentTarget.getBoundingClientRect()
        : null;
    if (!rect || !rect.width || !points.length) return;
    const x = ((event.clientX - rect.left) / rect.width) * 600;
    selected = points.reduce(
      (best, p, i) => (Math.abs(p.x - x) < Math.abs(points[best].x - x) ? i : best),
      0,
    );
  }
  function keydown(event: KeyboardEvent) {
    if (!points.length) return;
    if (event.key === "Escape") {
      selected = null;
      return;
    }
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    selected =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? points.length - 1
          : Math.max(
              0,
              Math.min(points.length - 1, (index ?? 0) + (event.key === "ArrowLeft" ? -1 : 1)),
            );
  }
  function date(day: string) {
    return new Intl.DateTimeFormat("ru-RU", {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    }).format(new Date(day + "T00:00:00Z"));
  }
</script>

<div
  class="plot"
  role="slider"
  aria-valuemin={0}
  aria-valuemax={Math.max(0, points.length - 1)}
  aria-valuenow={index ?? 0}
  aria-valuetext={points[index ?? 0]
    ? date(points[index ?? 0].day) +
      ": " +
      series.map((s) => s.label + " " + (s.points[index ?? 0]?.value ?? 0)).join(", ")
    : "Нет данных"}
  aria-label={label}
  tabindex="0"
  onpointermove={move}
  onpointerdown={move}
  onpointerleave={() => (selected = null)}
  onfocus={() => {
    if (selected === null) selected = points.length ? points.length - 1 : null;
  }}
  onblur={() => (selected = null)}
  onkeydown={keydown}
>
  <svg viewBox="0 0 600 180" preserveAspectRatio="none" aria-hidden="true">
    <defs
      ><linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color={series[0]?.color} stop-opacity="0.25" />
        <stop offset="100%" stop-color={series[0]?.color} stop-opacity="0" />
      </linearGradient></defs
    >
    {#each [24, 90, 156] as y}<line x1="24" x2="576" y1={y} y2={y} class="grid" />{/each}
    {#if series[0]?.area}<path d={series[0].area} fill={`url(#${gradient})`} />{/if}
    {#each series as s}
      <path
        d={s.path}
        fill="none"
        stroke={s.color}
        stroke-width="2"
        stroke-dasharray={s.dashed ? "4 3" : undefined}
        vector-effect="non-scaling-stroke"
      />
      {#each s.points.filter((p) => p.value > 0) as p (p.day)}
        <circle cx={p.x} cy={p.y} r="2.5" fill="var(--white)" stroke={s.color} stroke-width="1.5" />
      {/each}
    {/each}
    {#if point && index !== null}
      <line x1={point.x} x2={point.x} y1="12" y2="168" class="guide" />
      {#each series as s}
        {#if s.points[index]}<circle
            cx={s.points[index].x}
            cy={s.points[index].y}
            r="5"
            fill={s.color}
            stroke="var(--white)"
            stroke-width="2"
          />{/if}
      {/each}
    {/if}
  </svg>
  {#if point && index !== null}
    <div
      class="tooltip"
      role="tooltip"
      style:left={`clamp(8px, calc(${point.x / 6}% - 120px), max(8px, calc(100% - 248px)))`}
    >
      <div class="date">{date(point.day)}</div>
      {#each series as s}<div class="value">
          <span><i style:background={s.color}></i>{s.label}</span><strong
            >{new Intl.NumberFormat("ru-RU").format(s.points[index]?.value ?? 0)}</strong
          >
        </div>{/each}
    </div>
  {/if}
</div>

<style>
  .plot {
    position: relative;
    width: 100%;
    height: 180px;
    background: rgb(0 0 0 / 0.2);
    border: 1px solid var(--line);
    cursor: crosshair;
  }
  .plot:focus-visible {
    outline: 2px solid var(--mars-light);
    outline-offset: 3px;
  }
  svg {
    width: 100%;
    height: 100%;
    overflow: visible;
  }
  .grid {
    stroke: var(--line);
    stroke-opacity: 0.45;
    stroke-dasharray: 3 5;
  }
  .guide {
    stroke: var(--white);
    stroke-opacity: 0.5;
    stroke-dasharray: 4 4;
  }
  .tooltip {
    position: absolute;
    top: 8px;
    z-index: 2;
    width: 240px;
    box-sizing: border-box;
    max-width: calc(100% - 16px);
    padding: 10px 12px;
    border: 1px solid var(--line);
    border-radius: 6px;
    background: #191715;
    color: var(--white);
    box-shadow: 0 4px 16px #0008;
    pointer-events: none;
    font-size: 0.75rem;
  }
  .date {
    color: var(--dim);
    margin-bottom: 6px;
  }
  .value {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    line-height: 1.6;
  }
  .value span {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }
  strong {
    font-variant-numeric: tabular-nums;
  }
</style>
